import * as filestack from 'filestack-js';
import { ClientOptions } from 'filestack-js';
import React from 'react';
import { FailedFileUpload, UploadResponse } from './Filestack.types';
import { FileUploader, FileUploaderProps } from './FileUploader';

/**
 * Replace any non-word characters with dashes
 * @param filename
 */
const applyDefaultSanitization = (filename: string): string => filename.replace(/(?:\.(?![^.]+$)|[^\w.])+/g, '-');

export interface FilestackFileUploaderProps extends Omit<FileUploaderProps, 'handleFile'> {
  apiKey: string;
  clientOptions?: ClientOptions;
  onUploadDone: (files: UploadResponse[]) => void;
  onUploadStart?: () => void;
  onUploadFail?: (failedFile: File, error: Error | FailedFileUpload | unknown) => void;
  // Validate file metadata prior to upload e.g. size
  // Any error thrown during validation will be passed to
  // the onUploadFail handler.
  validateFile?: (file: File) => void | Error;
  // Apply further filename sanitization on top of default
  filenameSanitizer?: (filename: string) => string;
  accept?: string[];
  className?: string;
}

export const FilestackFileDropper: React.FC<FilestackFileUploaderProps> = ({
  apiKey,
  clientOptions,
  onUploadStart = () => undefined,
  onUploadDone,
  onUploadFail = () => undefined,
  validateFile = () => undefined,
  filenameSanitizer = (filename: string) => filename,
  ...props
}) => {
  const uploadFile = (selectedFile: File) => {
    onUploadStart();
    try {
      validateFile(selectedFile);
    } catch (err) {
      return onUploadFail(selectedFile, err);
    }

    filestack
      .init(apiKey, clientOptions)
      .upload(selectedFile, undefined, {
        filename: (file: File) =>
          // @ts-ignore
          filenameSanitizer(applyDefaultSanitization(file.name)),
        // Note we disable the built-in sanitizer in favour
        // of making modifications to the filename manually above as it
        // allows for more customisation.
        sanitizer: false,
      })
      .then((res: UploadResponse) => onUploadDone([res]))
      .catch(err => onUploadFail(selectedFile, err));
  };

  return <FileUploader handleFile={uploadFile} {...props} />;
};
